import { breakpoint } from '@zocdoc/components/src/grid';
import { flowRight, get, noop } from 'lodash';
import clickOutside from 'react-click-outside';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { LOCALIZED_STRINGS } from '../constants';
import React from 'react';
import { findDOMNode } from 'react-dom';
import styled from 'styled-components';
import { grey, grey10, grey25, grey50, grey75, navy, white } from '@zocdoc/branding/colors';
import { FontFamily } from '@zocdoc/components/src/typography';
import {
    ApplyButton,
    ApplySection,
    ClearAllOptionsButton,
    ClearAllOptionsButtonWrapper,
    ClearButton,
    FacetChip,
    FacetChipText,
    OptionCount,
} from '../styled-primitives';

const emptySet = new Set();

export class BaseMoreFilters extends React.Component {
    static propTypes = {
        moreFilters: PropTypes.arrayOf(PropTypes.shape({
            facetId: PropTypes.string,
            displayName: PropTypes.string,
            isMulti: PropTypes.bool,
            selectionRequired: PropTypes.bool,
            options: PropTypes.arrayOf(PropTypes.shape({
                optionId: PropTypes.string,
                displayName: PropTypes.string,
                count: PropTypes.number,
            })),
        })).isRequired,

        appliedOptionsMap: PropTypes.object,
        checkedOptionsMap: PropTypes.object,
        onApplyLanguages: PropTypes.func,
        onApplySeesChildren: PropTypes.func,
        onApplyTimeFilter: PropTypes.func,
        onApplyHospitalAffiliations: PropTypes.func,
        onCheckOption: PropTypes.func,
        onClearOptions: PropTypes.func,
        onHideDropdown: PropTypes.func,
        onSelectOption: PropTypes.func,
        onShowDropdown: PropTypes.func,
        onTapMoreFilters: PropTypes.func,
        onUncheckOption: PropTypes.func,
        updateParameters: PropTypes.func,
    };

    static defaultProps = {
        onApplyLanguages: noop,
        onApplySeesChildren: noop,
        onApplyTimeFilter: noop,
        onApplyHospitalAffiliations: noop,
        onCheckOption: noop,
        onClearOptions: noop,
        onHideDropdown: noop,
        onSelectOption: noop,
        onShowDropdown: noop,
        onTapMoreFilters: noop,
        onUncheckOption: noop,
        updateParameters: noop,
    };

    static getDerivedStateFromProps(props, state) {
        /**
         * Update state if we added or removed any facets from the dropdown
         */
        const isActive = props.moreFilters.some(({ facetId }) =>
            get(props.appliedOptionsMap[facetId], 'length', 0) > 0);

        if (isActive !== state.isActive) {
            return { isActive };
        }

        return null;
    }

    state = {
        isActive: false,
        showDropdown: false,
    };

    scrollableDropdownArea = React.createRef();

    componentDidUpdate(prevState) {
        if (!prevState.showDropdown && this.state.showDropdown) {
            const scrollableArea = findDOMNode(this.scrollableDropdownArea.current);
            if (scrollableArea) {
                scrollableArea.scrollTop = 0;
            }
        }
        return null;
    }

    componentWillUnmount() {
        if (this.state.showDropdown) {
            this.props.onHideDropdown(this);
        }
    }

    render() {
        return (
            <MoreFiltersWrapper>
                <FacetChip
                    isActive={this.state.isActive}
                    showDropdown={this.state.showDropdown}
                >
                    <FacetChipText
                        addCloseButtonPadding={this.state.isActive}
                        data-test="facet-chip-button"
                        isActive={this.state.isActive}
                        onClick={this.handleClickFacetChip}
                        showDropdown={this.state.showDropdown}
                    >
                        <FormattedMessage
                            {...LOCALIZED_STRINGS.MoreFiltersTitle}
                        />
                    </FacetChipText>
                    <ClearAllOptionsButtonWrapper
                        hidden={!this.state.isActive || this.props.selectionRequired}
                        onClick={this.handleClickClearAllOptions}
                        showDropdown={this.state.showDropdown}
                    >
                        <ClearAllOptionsButton color="currentColor" />
                    </ClearAllOptionsButtonWrapper>
                </FacetChip>
                {this.renderDropdown()}
            </MoreFiltersWrapper>
        );
    }

    renderDropdown() {
        return (
            <DropdownWrapper
                data-test="facet-dropdown"
                hidden={!this.state.showDropdown}
            >
                <FiltersSection ref={this.scrollableDropdownArea}>
                    {this.props.moreFilters.map((filter, index) => (
                        filter.options.length > 0 && this.renderFilter(filter, index)
                    ))}
                </FiltersSection>
                <ApplySection data-test="facet-dropdown-buttons">
                    <ApplyButton
                        data-test="facet-dropdown-apply-button"
                        onClick={this.handleApplyFacets}
                    >
                        apply
                    </ApplyButton>
                    <ClearButton
                        data-test="facet-dropdown-clear-button"
                        disabled={this.isOptionsClearButtonDisabled()}
                        onClick={this.handleClickClearButton}
                    >
                        clear
                    </ClearButton>
                </ApplySection>
            </DropdownWrapper>
        );
    }

    renderFilter(filter, idx) {
        const FilterWrapper = filter.isMulti ? 'div' : 'fieldset';
        return (
            <FilterWrapper
                key={`more-filters-${idx}`}
                data-test="facets-more-filters-filter"
            >
                <FilterName> {(filter.displayName).toLowerCase()} </FilterName>
                {filter.options.map((option, index) => (
                    <DropdownOption
                        key={option.optionId}
                        data-test="facet-more-filters-option"
                    >
                        <OptionLabel data-test="facet-more-filters-option-label">
                            {this.renderCorrectOptionType(filter, option, index)}
                            {option.displayName}
                        </OptionLabel>

                        {this.maybeRenderOptionCount(option)}
                    </DropdownOption>
                ))}
                {this.hideDivider(idx)}
            </FilterWrapper>
        );
    }

    hideDivider(index) {
        if (index === this.props.moreFilters.length - 1) {
            return (
                <FilterDivider hidden={true} />
            );
        } else {
            return (
                <FilterDivider />
            );
        }
    }

    maybeRenderOptionCount(option) {
        if (option.count) {
            return (
                <OptionCount data-test="facet-more-filters-count">
                    {option.count}
                </OptionCount>
            );
        } else {
            return (
                <OptionCount data-test="facet-more-filters-count" />
            );
        }
    }

    renderCorrectOptionType(filter, option) {
        const { facetId, isMulti } = filter;
        const { optionId } = option;
        const inputType = isMulti ? 'checkbox' : 'radio';
        const isChecked = this.getCheckedOptionsSet(facetId).has(option.optionId);
        const onChangeOption = isMulti
            ? this.handleChangeCheckbox : this.handleChangeRadio;
        const whichDataTest = isMulti ? 'facet-checkbox' : 'facet-radio';

        return (
            <StyledInput
                checked={isChecked}
                data-facet-id={facetId}
                data-option-id={optionId}
                data-test={whichDataTest}
                onChange={onChangeOption}
                type={inputType}
            />
        );
    }

    handleClickFacetChip = () => {
        this.setState(({ showDropdown }) => ({
            showDropdown: !showDropdown,
        }));
        if (!this.state.showDropdown) {
            this.props.onShowDropdown(this);
            this.props.onTapMoreFilters();
        } else {
            this.props.onHideDropdown(this);
            this.handleApplyFacets();
        }
    }

    handleApplyFacets = () => {
        let isActive = false;
        const newFilters = {};

        this.props.moreFilters.forEach(({ facetId }) => {
            const checkedOptions = this.getCheckedOptionsSet(facetId);

            if (checkedOptions.size) {
                isActive = true;
            }

            if (this.props.checkedOptionsMap[facetId]) {
                switch (facetId) {
                    case 'languages':
                        this.props.onApplyLanguages();
                        break;
                    case 'sees_children':
                        this.props.onApplySeesChildren();
                        break;
                    case 'special_hours':
                        this.props.onApplyTimeFilter();
                        break;
                    case 'hospital_affiliations':
                        this.props.onApplyHospitalAffiliations();
                        break;
                }
            }

            newFilters[facetId] = Array.from(checkedOptions);
        });

        this.props.updateParameters({ filters: newFilters });
        this.exitDropdown();

        if (isActive !== this.state.isActive) {
            this.setState({ isActive });
        }
    }

    handleChangeCheckbox = e => {
        const optionId = e.target.getAttribute('data-option-id');
        const facetId = e.target.getAttribute('data-facet-id');

        if (e.target.checked) {
            this.props.onCheckOption(facetId, optionId);
        } else {
            this.props.onUncheckOption(facetId, optionId);
        }
    }

    handleChangeRadio = e => {
        const optionId = e.target.getAttribute('data-option-id');
        const facetId = e.target.getAttribute('data-facet-id');

        this.props.onSelectOption(facetId, optionId);
    }

    handleClickClearAllOptions = () => {
        const facetsToClear = this.props.moreFilters.map(facet => facet.facetId);
        const newFilters = facetsToClear.reduce((acc, facetId) => {
            acc[facetId] = [];
            return acc;
        }, {});

        this.props.updateParameters({ filters: newFilters });
        this.props.onClearOptions(facetsToClear);

        this.setState({ isActive: false });

        if (this.state.showDropdown) {
            this.exitDropdown();
        }
    }

    handleClickClearButton = () => {
        const facetsToClear = this.props.moreFilters.map(facet => facet.facetId);
        this.props.onClearOptions(facetsToClear);
    }

    handleClickOutside() {
        if (this.state.showDropdown) {
            this.handleApplyFacets();
            this.exitDropdown();
        }
    }

    isOptionsClearButtonDisabled() {
        const hasCheckedOptions = this.props.moreFilters.some(({ facetId }) => this.getCheckedOptionsSet(facetId).size);
        return !hasCheckedOptions;
    }

    exitDropdown = () => {
        this.setState({
            showDropdown: false,
        });
        this.props.onHideDropdown(this);
    }

    getCheckedOptionsSet(facetId) {
        return this.props.checkedOptionsMap[facetId] || emptySet;
    }
}

const MoreFiltersWrapper = styled.div`
    display: inline-block;
    position: relative;
`;

const DropdownWrapper = styled.div`
    background-color: ${white};
    border-radius: 2px;
    border: solid 1px ${grey50};
    box-shadow: 0 1px 0 0 ${grey};
    display: flex;
    flex-direction: column;
    left: 0;
    margin-top: 8px;
    max-height: 400px;
    padding-top: 10px;
    position: absolute;
    top: 100%;
    z-index: 110;

    ${breakpoint('xsmall', { down: true })} {
        display: none;
    }
`;

const DropdownOption = styled.label`
    display: flex;
    padding-bottom: 5px;
    padding-left: 12px;
    padding-right: 12px;
    padding-top: 6px;
    white-space: nowrap;

    &:hover {
        background-color: ${grey10};
        cursor: pointer;
    }
`;

const FilterDivider = styled.div`
    border: solid 1px ${grey25};
    margin-top: 15px;
    margin-bottom: 15px;
    width: 100%;
`;

const OptionLabel = styled.div`
    color: ${navy};
    font: 15px ${FontFamily.SharpSansMedium};
    margin-right: 16px;
    white-space: nowrap;
    ${breakpoint('small', { only: true })} {
        margin-right: 5px;
        max-width: 325px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const StyledInput = styled.input`
    margin-right: 10px;
`;

const FiltersSection = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    overflow: auto;
`;

const FilterName = styled.div`
    color: ${grey75};
    font: 15px ${FontFamily.SharpSansSemibold};
    padding-bottom: 3px;
    padding-left: 15px;
`;

export default flowRight(
    injectIntl,
    clickOutside,
)(BaseMoreFilters);
